import Information16 from '@carbon/icons-react/es/information/16';
import Search16 from '@carbon/icons-react/es/search/16';
import { List, Tooltip, Typography } from 'antd';
import { ListItemMetaProps } from 'antd/lib/list/Item';
import { initial } from 'lodash';
import moment from 'moment';
import React, { FC, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { components, OptionProps, OptionTypeBase } from 'react-select';
import styled, { css } from 'styled-components';
import { MemberType, UserStatus } from 'api/models';
import { TimeoffResponse } from 'api/timeoff/types';
import { Avatar } from 'components/atoms/avatar';
import { Control, Select, SelectProps } from 'components/atoms/select';
import { TitleWrapper } from 'components/atoms/select/custom-components';
import { GetTimeOffTooltipContent } from 'components/atoms/time-off-tooltip';
import { PlaceholderSelectTitle } from 'components/organisms/placeholders/select-group-title';
import { SingleLineEllipsis } from 'components/styles';
import { useTranslation } from 'hooks/use-translation';
import { useUsersMapQuery } from 'queries/members';
import { getStartDate, getTimeOffArray } from 'utils/time-off-utils';
import { PlaceholderAvatar } from '../placeholder-avatar';
import { Option, UserOption } from '../squad-picker/types';
import { userOption } from '../squad-picker/utils';
import { AssigneeOptionHeader } from './assignee-option-header';
import { BaseProps } from './assignee-picker';
import { VirtualizedMenuList } from './virtualised-list';

const selectStyles = {
  menu: () => ({
    boxShadow: 'none',
    flex: 1,
    overflow: 'hidden',
    marginTop: 'var(--spacing-05) !important',
  }),
  menuList: (provided) => ({
    ...provided,
    paddingTop: 0,
    paddingBottom: 0,
    maxHeight: initial,
    height: '100%',
  }),
  control: (provided) => ({ ...provided, margin: '0 var(--spacing-05)' }),
  group: (provided) => ({ ...provided, paddingTop: 0, paddingBottom: 0 }),
  groupHeading: (provided) => ({ ...provided, marginBottom: 0, flex: 1 }),
  option: (provided) => ({
    ...provided,
    paddingLeft: 'var(--spacing-05)',
    paddingRight: 'var(--spacing-05)',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    display: 'flex !important',
  }),
};

const GroupTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
`;
const GroupTitleText = styled(Typography.Text).attrs({ ellipsis: true })`
  color: var(--gray-50);
`;
const GroupTitle = ({ id, label, count, description }) => {
  return (
    <GroupTitleWrapper>
      <GroupTitleText>
        {label} {id === 'assignees' ? `(${count})` : null}
      </GroupTitleText>
      {description != null ? (
        <Tooltip title={description}>
          <Information16 />
        </Tooltip>
      ) : null}
    </GroupTitleWrapper>
  );
};

export const GroupHeading = (props) => {
  const {
    data,
    label,
    onPlaceholderCreateClick,
    hidePlaceholderCreate,
    clearSelection,
    groupHeadingComponent: GroupHeadingComponent = components.GroupHeading,
  } = props;

  return (
    <GroupHeadingComponent {...props}>
      {data?.id === 'teams' ? (
        <PlaceholderSelectTitle
          onClick={onPlaceholderCreateClick}
          hideCreate={hidePlaceholderCreate}
        />
      ) : data?.id === 'assignees' ? (
        <AssigneeOptionHeader
          count={data?.options?.length ?? 0}
          onClearAll={clearSelection}
        />
      ) : (
        <GroupTitle
          id={data?.id}
          label={label}
          description={data?.description}
          count={data?.options?.length ?? 0}
        />
      )}
    </GroupHeadingComponent>
  );
};

const DropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}>
    <Search16 />
  </components.DropdownIndicator>
);

const ListItem = styled(List.Item)`
  padding: 0;

  .ant-list-item-main {
    flex: 1;
  }
`;

type MetaProps = ListItemMetaProps & {
  isFocused: boolean;
  isSelected: boolean;
};

const ListItemMeta = styled(List.Item.Meta)<MetaProps>`
  align-items: center;
  & .ant-list-item-meta-title {
    margin-bottom: 0;
    ${SingleLineEllipsis};
    ${({ isSelected }) =>
      isSelected
        ? css`
            color: var(--white-100) !important;
          `
        : css`
            color: var(--gray-100) !important;
          `};
  }
  & .ant-list-item-meta-description {
    font-size: var(--font-size-sm);
    ${({ isSelected }) =>
      isSelected
        ? css`
            color: var(--white-100);
          `
        : css`
            color: var(--text-02);
          `};
    font-weight: normal;
  }
`;

const Deactivated = styled.span`
  color: var(--error-color);
`;

const CustomOption = (props: OptionProps<Option, true>): JSX.Element => {
  const {
    data,
    isSelected,
    isFocused,
    selectProps: { dueDate, startDate, placeholderResolutions },
  } = props;

  let timeOffArray: TimeoffResponse[] | undefined = [];

  const modifiedStartDate = startDate
    ? moment(startDate)
    : getStartDate(dueDate);

  if (!!dueDate && !!data.extra.timeOffDetails) {
    timeOffArray = getTimeOffArray(
      modifiedStartDate,
      moment(dueDate),
      data.extra.timeOffDetails,
    );
  }

  const timeOffText = (
    <GetTimeOffTooltipContent
      timeOff={timeOffArray}
      userName={data.label}
      startDate={modifiedStartDate}
      dueDate={moment(dueDate)}
    />
  );
  const { data: usersMap, isLoading } = useUsersMapQuery();

  /**
   * This takes care of rendering user option instead of a team
   * when the team is already resolved to an user.
   */
  const resolvedUserOption = useMemo<UserOption | null>(() => {
    const teamResolvedTo =
      data.extra?.type === 'team' &&
      placeholderResolutions?.mappings?.[data.extra.id];

    const user =
      isSelected &&
      data.extra?.type === 'team' &&
      teamResolvedTo &&
      teamResolvedTo.identityType === 'USER' &&
      !isLoading
        ? usersMap?.get(teamResolvedTo.userId)
        : null;

    return user ? userOption(user) : null;
  }, [
    data.extra.id,
    data.extra?.type,
    isLoading,
    isSelected,
    placeholderResolutions,
    usersMap,
  ]);

  const _data = useMemo(
    () => resolvedUserOption || data,
    [data, resolvedUserOption],
  );
  /**
   * this prop is used to render the avatar of the user
   * resolved for a placeholder.
   */
  const extra = useMemo(() => {
    const teamResolvedTo =
      data.extra?.type === 'team' &&
      placeholderResolutions?.mappings?.[data.extra.id];

    const user =
      !isSelected &&
      data.extra?.type === 'team' &&
      teamResolvedTo &&
      teamResolvedTo.identityType === 'USER' &&
      !isLoading
        ? usersMap?.get(teamResolvedTo.userId)
        : null;

    return user ? (
      <Avatar
        name={user.firstName}
        src={user.photoUrl}
        companyLogoUrl={user.company.companyLogoUrl}
      />
    ) : null;
  }, [
    data.extra.id,
    data.extra?.type,
    isLoading,
    isSelected,
    placeholderResolutions,
    usersMap,
  ]);

  return (
    <components.Option {...props}>
      <ListItem extra={extra}>
        <ListItemMeta
          isFocused={isFocused}
          isSelected={isSelected}
          avatar={
            _data.extra?.type === 'team' && !resolvedUserOption ? (
              <PlaceholderAvatar
                size="default"
                isCustomerRole={
                  _data.extra?.teamType === MemberType.External ||
                  _data?.teamType === MemberType.External
                }
                name={_data.__isNew__ ? _data.value : _data.label}
                companyLogoUrl={_data.logoUrl}
              />
            ) : (
              <Avatar
                name={_data.__isNew__ ? _data.value : _data.label}
                src={_data.imageUrl}
                companyLogoUrl={_data.logoUrl}
              />
            )
          }
          title={
            <TitleWrapper
              title={_data.label}
              timeOffText={timeOffText}
              timeOffArray={timeOffArray}
            />
          }
          description={
            _data.userStatus === UserStatus.Inactive ? (
              <Deactivated>
                <FormattedMessage id="deactivated" />
              </Deactivated>
            ) : (
              _data.description
            )
          }
        />
      </ListItem>
    </components.Option>
  );
};

interface CustomProps {
  onPlaceholderCreateClick: () => void;
  clearSelection: () => void;
}

export const AssigneePickerSelect: FC<
  Pick<BaseProps, 'hidePlaceholderCreate'> &
    SelectProps<OptionTypeBase, true> &
    CustomProps
> = (props) => {
  const {
    components: customComponents,
    hidePlaceholderCreate,
    clearSelection,
    onPlaceholderCreateClick,
    placeholderResolutions,
    isLoadingPlaceholderResolutions,
    dueDate,
    startDate,
    ...rest
  } = props;

  const MenuList = useCallback(
    (_props) => (
      <VirtualizedMenuList
        {..._props}
        groupHeadingExtraProps={{
          clearSelection,
          hidePlaceholderCreate,
          onPlaceholderCreateClick,
        }}
        placeholderResolutions={placeholderResolutions}
        isLoadingPlaceholderResolutions={isLoadingPlaceholderResolutions}
      />
    ),
    [
      clearSelection,
      hidePlaceholderCreate,
      onPlaceholderCreateClick,
      placeholderResolutions,
      isLoadingPlaceholderResolutions,
    ],
  );

  return (
    <Select
      autoFocus
      isMulti
      menuIsOpen
      backspaceRemovesValue={false}
      components={{
        ClearIndicator: null,
        IndicatorSeparator: null,
        DropdownIndicator,
        Control,
        Option: CustomOption,
        MenuList,
        ...customComponents,
      }}
      controlShouldRenderValue={false}
      hideSelectedOptions={false}
      isClearable
      placeholder={useTranslation('placeholder.search')}
      tabSelectsValue={false}
      styles={selectStyles}
      {...rest}
      grouped
      type="user"
      dueDate={dueDate}
      startDate={startDate}
      placeholderResolutions={placeholderResolutions}
    />
  );
};
